import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getAuditLog, getWalletApiHealth } from '@/services/api';
import { SectionHeader } from '@/components/common/Page';
import { useAdminI18n } from '@/contexts/AdminI18nContext';
import { useAdminTime } from '@/contexts/AdminTimeContext';
import type { AuditLogEntry } from '@/types';

function summarizeValue(value: string | null) {
  if (!value) return '-';

  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    const keys = Object.keys(parsed).slice(0, 4);
    return keys.map((key) => `${key}: ${String(parsed[key])}`).join(' | ') || value;
  } catch {
    return value;
  }
}

function formatAdmin(entry: AuditLogEntry) {
  return entry.admin_username ? `@${entry.admin_username}` : `#${entry.admin_id}`;
}

export default function AdminOperations() {
  const { t } = useAdminI18n();
  const { formatDateTime } = useAdminTime();

  const healthQuery = useQuery({
    queryKey: ['admin-wallet-api-health'],
    queryFn: getWalletApiHealth,
    refetchInterval: 60000,
  });

  const auditQuery = useQuery({
    queryKey: ['admin-audit-log'],
    queryFn: () => getAuditLog(100, 0),
  });

  return (
    <Box>
      <SectionHeader
        title={t('admin.operations.title')}
        detail={t('admin.operations.detail')}
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              healthQuery.refetch();
              auditQuery.refetch();
            }}
          >
            {t('admin.operations.refresh')}
          </Button>
        }
      />

      <SectionHeader
        title={t('admin.operations.walletApis')}
        detail={
          healthQuery.data?.checked_at
            ? t('admin.operations.checkedAt', { date: formatDateTime(healthQuery.data.checked_at) })
            : undefined
        }
      />
      {healthQuery.isLoading ? (
        <CircularProgress size={24} />
      ) : healthQuery.error ? (
        <Alert severity="error">{t('admin.operations.healthLoadError')}</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {healthQuery.data?.apis.map((api) => (
            <Card key={api.network} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2">{api.network}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                    {api.api_base || '-'}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color={api.ok ? 'success' : 'error'}
                  label={api.ok ? t('admin.operations.statusOk') : t('admin.operations.statusIssue')}
                />
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Chip size="small" label={`${t('admin.operations.configured')}: ${api.configured ? t('admin.common.verified') : t('admin.common.rejected')}`} />
                <Chip size="small" label={`${t('admin.operations.reachable')}: ${api.reachable ? t('admin.common.verified') : t('admin.common.rejected')}`} />
                <Chip size="small" label={`${t('admin.operations.authorized')}: ${api.authorized ? t('admin.common.verified') : t('admin.common.rejected')}`} />
                <Chip size="small" label={`HTTP ${api.status ?? '-'}`} />
                <Chip size="small" label={`${api.latency_ms ?? '-'} ms`} />
              </Stack>
              {api.error && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1, overflowWrap: 'anywhere' }}>
                  {api.error}
                </Typography>
              )}
            </Card>
          ))}
        </Box>
      )}

      <SectionHeader
        title={t('admin.operations.auditLog')}
        detail={auditQuery.data ? t('admin.common.total', { count: auditQuery.data.total }) : undefined}
      />
      {auditQuery.isLoading ? (
        <CircularProgress size={24} />
      ) : auditQuery.error ? (
        <Alert severity="error">{t('admin.operations.auditLoadError')}</Alert>
      ) : (auditQuery.data?.audit_log.length ?? 0) === 0 ? (
        <Alert severity="info">{t('admin.operations.noAuditLogs')}</Alert>
      ) : (
        <TableContainer component={Card}>
          <Table size="small" sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.operations.time')}</TableCell>
                <TableCell>{t('admin.operations.admin')}</TableCell>
                <TableCell>{t('admin.operations.action')}</TableCell>
                <TableCell>{t('admin.operations.entity')}</TableCell>
                <TableCell>{t('admin.operations.change')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditQuery.data?.audit_log.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{formatDateTime(entry.created_at)}</TableCell>
                  <TableCell>{formatAdmin(entry)}</TableCell>
                  <TableCell>{entry.action}</TableCell>
                  <TableCell>
                    {entry.entity_type}
                    {entry.entity_id ? ` #${entry.entity_id}` : ''}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                      {summarizeValue(entry.new_value)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
